/**
 * PipelinePicker — combobox over the tenant's pipelines with inline "+ Create".
 *
 * Follows the entity-picker inline-create policy
 * (docs/design-system/entity-picker-inline-create.md): a pipeline is a
 * tenant-scoped, user-creatable entity, so the picker offers inline create via
 * PipelineQuickCreateDialog (skipNavigation + onCreated → auto-selects the new
 * row). Pattern reference: ContactPicker / OrganizationPicker.
 *
 * Stage selection is intentionally NOT an inline-create picker — see
 * DealQuickCreateDialog (a stage's position/type/probability/gate-criteria are
 * funnel design that only makes sense on the Pipelines page).
 */
import { useState, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, GitBranch } from 'lucide-react';
import { cn } from '../../../lib/utils';
import { EntityPickerBase } from '../../../shared/components/common';
import { filterAndSortBySearch } from '../../../shared/utils/searchScore';
import { usePipelines } from '../hooks/usePipelines';
import { PipelineQuickCreateDialog } from './PipelineQuickCreateDialog';
import type { Pipeline } from '../types';

interface PipelinePickerProps {
  /** Currently selected pipeline ID */
  value: string;
  /** Callback when a pipeline is selected */
  onChange: (pipelineId: string) => void;
  /** Callback returning the selected pipeline object */
  onPipelineSelected?: (pipeline: Pipeline | null) => void;
  /** Optional label to display above the picker */
  label?: string;
  /** Placeholder text when no pipeline is selected */
  placeholder?: string;
  /** Disable the picker */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Whether the field is required (shows * on label) */
  required?: boolean;
  /** Error state — red border on the trigger */
  error?: boolean;
  /** Show the "None" option to clear selection */
  allowClear?: boolean;
  /** Hide the "Add new" action (useful in filter contexts) */
  hideAddNew?: boolean;
  /**
   * DOM id for the picker's trigger. A caller with a sibling `<Label>` uses it
   * to associate the two, and it is the stable hook a browser test selects the
   * control by instead of a positional `getByRole('combobox')` index.
   */
  triggerId?: string;
}

export function PipelinePicker({
  value,
  onChange,
  onPipelineSelected,
  label,
  placeholder,
  disabled,
  className,
  required = false,
  error = false,
  allowClear = false,
  hideAddNew = false,
  triggerId,
}: PipelinePickerProps) {
  const { t } = useTranslation('pipeline');
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [createDialogName, setCreateDialogName] = useState('');

  const { data: pipelinesData } = usePipelines();

  const pipelines = useMemo(() => pipelinesData ?? [], [pipelinesData]);

  // Client-side filter — the list endpoint returns all pipelines (few per tenant).
  const filtered = useMemo(
    () => filterAndSortBySearch(pipelines, search, (p) => p.name),
    [pipelines, search],
  );

  const handleSelect = useCallback(
    (pipelineId: string) => {
      const pipeline = pipelines.find((p) => p.id === pipelineId) ?? null;
      onChange(pipelineId);
      onPipelineSelected?.(pipeline);
      setOpen(false);
      setSearch('');
    },
    [pipelines, onChange, onPipelineSelected],
  );

  const handleClear = useCallback(() => {
    onChange('');
    onPipelineSelected?.(null);
    setOpen(false);
    setSearch('');
  }, [onChange, onPipelineSelected]);

  const handleAddNew = useCallback(() => {
    setCreateDialogName(search.trim());
    setOpen(false);
    setShowCreateDialog(true);
  }, [search]);

  const handlePipelineCreated = useCallback(
    (pipeline: Pipeline) => {
      onChange(pipeline.id);
      onPipelineSelected?.(pipeline);
      setSearch('');
      setShowCreateDialog(false);
    },
    [onChange, onPipelineSelected],
  );

  const addNewLabel = search.trim()
    ? t('pipelinePicker.createPipelineWithName', { name: search.trim() })
    : t('pipelinePicker.addNewPipeline');

  return (
    <>
      <EntityPickerBase<Pipeline>
        triggerId={triggerId}
        value={value}
        items={filtered}
        getItemLabel={(pipeline) => pipeline.name}
        renderItem={(pipeline, isSelected) => (
          <>
            <Check
              className={cn('me-2 h-4 w-4', isSelected ? 'opacity-100' : 'opacity-0')}
            />
            <GitBranch className="me-2 h-4 w-4 text-muted-foreground shrink-0" />
            <span className="truncate">{pipeline.name}</span>
          </>
        )}
        label={label}
        placeholder={placeholder ?? t('pipelinePicker.searchOrCreate')}
        searchPlaceholder={t('pipelinePicker.searchPipelines')}
        emptyMessage={t('pipelinePicker.noPipelinesFound')}
        disabled={disabled}
        className={cn(error && '[&_button[role=combobox]]:border-destructive', className)}
        required={required}
        search={search}
        onSearchChange={setSearch}
        open={open}
        onOpenChange={setOpen}
        onSelect={handleSelect}
        allowClear={allowClear}
        onClear={handleClear}
        addNewLabel={addNewLabel}
        onAddNew={hideAddNew ? undefined : handleAddNew}
      />

      <PipelineQuickCreateDialog
        open={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onCreated={handlePipelineCreated}
        defaultName={createDialogName}
      />
    </>
  );
}
